(() => {
  const globalKey = '__rawjoyGridAutoplay';
  const selector = '[data-grid-autoplay]';

  if (window[globalKey]) {
    window[globalKey].scan();
    return;
  }

  const mobileQuery = window.matchMedia('(max-width: 767px)');
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();

  class GridAutoplay {
    constructor(root) {
      this.root = root;
      this.inner = root.querySelector('.swipe__inner');
      this.mobileInterval = Number(root.dataset.gridAutoplayMobileInterval) || 4000;
      this.desktopInterval = Number(root.dataset.gridAutoplayDesktopInterval) || 5000;
      this.duration = Number(root.dataset.gridAutoplayDuration) || 1400;
      this.timer = null;
      this.animationFrame = null;
      this.direction = 1;
      this.visible = true;
      this.interacting = false;

      this.onInteractionStart = this.onInteractionStart.bind(this);
      this.onInteractionEnd = this.onInteractionEnd.bind(this);
      this.onEnvironmentChange = this.onEnvironmentChange.bind(this);
      this.onScrollEnd = this.onScrollEnd.bind(this);

      root.addEventListener('pointerdown', this.onInteractionStart, { passive: true });
      root.addEventListener('pointerup', this.onInteractionEnd, { passive: true });
      root.addEventListener('pointercancel', this.onInteractionEnd, { passive: true });
      root.addEventListener('touchstart', this.onInteractionStart, { passive: true });
      root.addEventListener('touchend', this.onInteractionEnd, { passive: true });
      root.addEventListener('focusin', this.onInteractionStart);
      root.addEventListener('focusout', this.onInteractionEnd);
      root.addEventListener('scrollend', this.onScrollEnd, { passive: true });
      mobileQuery.addEventListener('change', this.onEnvironmentChange);
      reducedMotionQuery.addEventListener('change', this.onEnvironmentChange);

      this.prepareLoop();

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.visible = entries[0]?.isIntersecting ?? true;
          this.schedule();
        }, { threshold: 0.2 });
        this.observer.observe(root);
      }

      this.schedule();
    }

    get items() {
      return this.inner ? [...this.inner.children].filter((item) => item instanceof HTMLElement) : [];
    }

    get originalItems() {
      return this.items.filter((item) => !item.hasAttribute('data-grid-autoplay-clone'));
    }

    get loopClone() {
      return this.inner?.querySelector('[data-grid-autoplay-clone]') || null;
    }

    get interval() {
      return mobileQuery.matches ? this.mobileInterval : this.desktopInterval;
    }

    viewportEnabled() {
      return mobileQuery.matches
        ? this.root.hasAttribute('data-grid-autoplay-mobile')
        : this.root.hasAttribute('data-grid-autoplay-desktop');
    }

    loopEnabled() {
      return mobileQuery.matches && this.root.hasAttribute('data-grid-autoplay-mobile');
    }

    prepareLoop() {
      if (!this.inner) return;

      this.inner.querySelectorAll('[data-grid-autoplay-clone]').forEach((clone) => clone.remove());
      this.inner.style.removeProperty('justify-content');

      if (!this.loopEnabled()) return;

      const originals = this.originalItems;
      if (originals.length < 2) return;

      this.inner.style.setProperty('justify-content', 'flex-start', 'important');
      originals.forEach((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('data-grid-autoplay-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.removeAttribute('id');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        this.inner.appendChild(clone);
      });

      this.root.scrollLeft = 0;
      requestAnimationFrame(() => {
        this.root.scrollLeft = 0;
      });
    }

    isActive() {
      return this.root.isConnected
        && this.viewportEnabled()
        && !reducedMotionQuery.matches
        && !document.hidden
        && this.visible
        && !this.interacting
        && this.items.length > 1
        && this.root.scrollWidth > this.root.clientWidth + 2;
    }

    normalizedOffset(item, firstItem) {
      return Math.max(0, item.offsetLeft - firstItem.offsetLeft);
    }

    targets() {
      const loopActive = this.loopEnabled() && this.loopClone;
      const items = loopActive
        ? [...this.originalItems, this.loopClone]
        : this.items;
      if (!items.length) return [];
      const maxScroll = Math.max(0, this.root.scrollWidth - this.root.clientWidth);
      const firstItem = items[0];
      const targets = items
        .map((item) => this.normalizedOffset(item, firstItem))
        .filter((target) => target <= maxScroll + 1);
      if (loopActive) return [...new Set(targets)];

      const lastTarget = targets.at(-1) ?? 0;

      if (maxScroll - lastTarget > 32) targets.push(maxScroll);
      return [...new Set(targets)];
    }

    next() {
      if (!this.isActive()) {
        this.schedule();
        return;
      }

      const targets = this.targets();
      const current = this.root.scrollLeft;
      if (targets.length < 2) {
        this.schedule();
        return;
      }

      const currentIndex = targets.reduce((nearest, target, index) => (
        Math.abs(target - current) < Math.abs(targets[nearest] - current) ? index : nearest
      ), 0);

      if (this.loopEnabled() && this.loopClone) {
        if (currentIndex >= targets.length - 1) {
          this.root.scrollLeft = targets[0];
          this.animateTo(targets[1], targets, false);
          return;
        }

        const nextIndex = currentIndex + 1;
        this.animateTo(targets[nextIndex], targets, nextIndex === targets.length - 1);
        return;
      }

      if (currentIndex >= targets.length - 1) this.direction = -1;
      else if (currentIndex <= 0) this.direction = 1;

      const nextIndex = Math.max(0, Math.min(targets.length - 1, currentIndex + this.direction));
      this.animateTo(targets[nextIndex], targets);
    }

    animateTo(target, targets, resetAfter = false) {
      const start = this.root.scrollLeft;
      const distance = target - start;
      const startTime = performance.now();
      const normalStep = Math.abs((targets[1] ?? target) - (targets[0] ?? start)) || Math.abs(distance) || 1;
      const animationDuration = Math.max(700, Math.min(2200, this.duration * Math.abs(distance) / normalStep));

      this.root.classList.add('grid-autoplay--animating');

      const tick = (time) => {
        if (this.interacting || !this.root.isConnected) {
          this.root.classList.remove('grid-autoplay--animating');
          this.animationFrame = null;
          return;
        }

        const progress = Math.min(1, (time - startTime) / animationDuration);
        const eased = 0.5 - Math.cos(Math.PI * progress) / 2;
        this.root.scrollLeft = start + distance * eased;

        if (progress < 1) this.animationFrame = requestAnimationFrame(tick);
        else {
          this.root.scrollLeft = target;
          if (resetAfter) this.root.scrollLeft = 0;
          this.animationFrame = null;
          requestAnimationFrame(() => {
            this.root.classList.remove('grid-autoplay--animating');
            this.schedule();
          });
        }
      };

      this.animationFrame = requestAnimationFrame(tick);
    }

    stop() {
      window.clearTimeout(this.timer);
      this.timer = null;
      if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
      this.root.classList.remove('grid-autoplay--animating');
    }

    schedule() {
      this.stop();
      if (this.isActive()) this.timer = window.setTimeout(() => this.next(), this.interval);
    }

    onInteractionStart() {
      this.interacting = true;
      this.stop();
    }

    onInteractionEnd() {
      this.interacting = false;
      this.schedule();
    }

    onScrollEnd() {
      if (!this.loopEnabled() || !this.loopClone || this.animationFrame) return;
      const firstItem = this.originalItems[0];
      if (!firstItem) return;
      const loopTarget = this.normalizedOffset(this.loopClone, firstItem);
      if (this.root.scrollLeft >= loopTarget - 2) this.root.scrollLeft = 0;
    }

    onEnvironmentChange() {
      this.prepareLoop();
      this.schedule();
    }
  }

  const api = {
    scan(scope = document) {
      const roots = [
        ...(scope.matches?.(selector) ? [scope] : []),
        ...scope.querySelectorAll(selector),
      ];
      roots.forEach((root) => {
        const controller = controllers.get(root);
        if (controller) controller.schedule();
        else controllers.set(root, new GridAutoplay(root));
      });
    },
  };

  window[globalKey] = api;
  api.scan();

  document.addEventListener('visibilitychange', () => api.scan());
  document.addEventListener('shopify:section:load', (event) => api.scan(event.target));
})();

